export const ADD_TENOR_TABLE_COLUMNS = {
  PRODUCT: "product_id",
  CONTRACT: "contract_id",
  STYLE: "style_id",
  PERIOD: "period_type_id",
  LINK: "link",
  TENOR: "style_period_tenor_id",
  SUB_LEG: "sub_leg",
  START_DATE: "start_date",
  END_DATE: "end_date",
  UL_TENOR: "ul_tenor",
  MONTHS_IN_TENOR: "months_in_tenor",
};


/** Header labels aligned with the Excel tenor upload sheet */
export const ADD_TENOR_TABLE_LABELS = {
  [ADD_TENOR_TABLE_COLUMNS.PRODUCT]: "Product",
  [ADD_TENOR_TABLE_COLUMNS.CONTRACT]: "Contract",
  [ADD_TENOR_TABLE_COLUMNS.STYLE]: "Style",
  [ADD_TENOR_TABLE_COLUMNS.PERIOD]: "Period",
  [ADD_TENOR_TABLE_COLUMNS.LINK]: "Link",
  [ADD_TENOR_TABLE_COLUMNS.TENOR]: "Tenor",
  [ADD_TENOR_TABLE_COLUMNS.SUB_LEG]: "Sub-leg",
  [ADD_TENOR_TABLE_COLUMNS.START_DATE]: "Start Date",
  [ADD_TENOR_TABLE_COLUMNS.END_DATE]: "End Date",
  [ADD_TENOR_TABLE_COLUMNS.UL_TENOR]: "UL Tenor",
  [ADD_TENOR_TABLE_COLUMNS.MONTHS_IN_TENOR]: "# Months in Tenor",
};

/** Header hover hints — only columns whose meaning isn't obvious from the label. */
export const ADD_TENOR_TABLE_HEADER_TOOLTIPS = {
  [ADD_TENOR_TABLE_COLUMNS.LINK]:
    "Expiry calendar. One page shows as soon as the period loads. Calendars with early-expiry products stay blank until a tenor is selected, then open that tenor's page.",
  [ADD_TENOR_TABLE_COLUMNS.START_DATE]:
    "Start date of the closing window for the shares",
  [ADD_TENOR_TABLE_COLUMNS.END_DATE]: "Expiry date",
};

/** Grey row-number gutter (Excel row header) */
export const ADD_TENOR_EXCEL_ROW_GUTTER_WIDTH = 44;

/** Column order and widths (px). Link sits after Tenor. */
export const ADD_TENOR_EXCEL_COLUMNS = [
  { key: ADD_TENOR_TABLE_COLUMNS.PRODUCT, width: 112, pcsLocked: true },
  { key: ADD_TENOR_TABLE_COLUMNS.CONTRACT, width: 104, pcsLocked: true },
  { key: ADD_TENOR_TABLE_COLUMNS.STYLE, width: 132, pcsLocked: true },
  { key: ADD_TENOR_TABLE_COLUMNS.PERIOD, width: 100 },
  { key: ADD_TENOR_TABLE_COLUMNS.TENOR, width: 100 },
  { key: ADD_TENOR_TABLE_COLUMNS.LINK, width: 88 },
  { key: ADD_TENOR_TABLE_COLUMNS.SUB_LEG, width: 200 },
  { key: ADD_TENOR_TABLE_COLUMNS.START_DATE, width: 140 },
  { key: ADD_TENOR_TABLE_COLUMNS.END_DATE, width: 140 },
  { key: ADD_TENOR_TABLE_COLUMNS.UL_TENOR, width: 100 },
];

export const ADD_TENOR_EXCEL_DATA_WIDTH = ADD_TENOR_EXCEL_COLUMNS.reduce(
  (sum, c) => sum + c.width,
  0
);

export const ADD_TENOR_EXCEL_TABLE_WIDTH =
  ADD_TENOR_EXCEL_DATA_WIDTH + ADD_TENOR_EXCEL_ROW_GUTTER_WIDTH;

/** Horizontal space for modal chrome (body padding + borders) */
export const ADD_TENOR_MODAL_WIDTH_EXTRA = 128;

/** First period option from the period-types API (default selection). */
export function defaultPeriodFieldsFromOptions(periodOptions) {
  const first = periodOptions?.[0];
  if (!first) return {};
  return {
    period_type_id: first.value,
    period_label: first.label,
  };
}

export const ADD_TENOR_MODAL_WIDTH =
  ADD_TENOR_EXCEL_TABLE_WIDTH + ADD_TENOR_MODAL_WIDTH_EXTRA;

/** Relative column width for fluid table layout (no horizontal scroll) */
export function addTenorExcelColWidthPercent(pixelWidth) {
  return `${(pixelWidth / ADD_TENOR_EXCEL_TABLE_WIDTH) * 100}%`;
}

/** Split shortcut label into product / contract / style columns for display */
export function splitShortcutDisplayName(name) {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) {
    return { product: "—", contract: "—", style: "—" };
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 3) {
    return {
      product: parts[0],
      contract: parts[1],
      style: parts.slice(2).join(" "),
    };
  }
  if (parts.length === 2) {
    return { product: parts[0], contract: parts[1], style: "—" };
  }
  return { product: trimmed, contract: "—", style: "—" };
}

/** Resolve PCS shortcut id → product / contract / style ids for API calls. */
export function pcsLockedRowOverrides(pcsId, shortcutLabel) {
  const split = splitShortcutDisplayName(shortcutLabel);
  return {
    product_contract_style_id: parseAddTenorCatalogId(pcsId),
    style_label: shortcutLabel,
    product_label: split.product,
    contract_label: split.contract,
    style_display: split.style,
  };
}

/** Positive catalog id, or null when unset (never coerces null/"" to 0). */
export function parseAddTenorCatalogId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * @param {object} row
 * @returns {{ product_id: number, contract_id: number, style_id: number } | null}
 */
export function buildAddTenorCatalogParams(row) {
  if (!row) return null;
  const product_id = parseAddTenorCatalogId(row.product_id);
  const contract_id = parseAddTenorCatalogId(row.contract_id);
  const style_id = parseAddTenorCatalogId(row.style_id);
  if (product_id == null || contract_id == null || style_id == null) {
    return null;
  }
  return { product_id, contract_id, style_id };
}

export const EXISTING_TENOR_UPDATE_MESSAGE =
  "This tenor already exists. Proceeding will update it.";

/** Resets fields that depend on period / tenor / missing-tenors API data. */
export function createAddTenorDerivedFieldReset(overrides = {}) {
  return {
    style_period_tenor_id: null,
    tenor_exists: false,
    tenor_label: "",
    tenor_date: null,
    ul_tenor: "",
    sub_leg: "",
    sub_legs: [],
    sublegs_from_api: false,
    tenor_group_id: null,
    sub_leg_index: 0,
    enable_start_date: false,
    months_in_tenor: 1,
    start_date: null,
    end_date: null,
    ...overrides,
  };
}

export function createEmptyAddTenorRow(overrides = {}) {
  return {
    key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    product_id: null,
    contract_id: null,
    style_id: null,
    product_contract_style_id: null,
    period_type_id: null,
    style_period_tenor_id: null,
    tenor_exists: false,
    tenor_label: "",
    tenor_date: null,
    sub_leg: "",
    sub_legs: [],
    start_date: null,
    end_date: null,
    ul_tenor: "",
    months_in_tenor: 1,
    sublegs_from_api: false,
    tenor_group_id: null,
    sub_leg_index: 0,
    enable_start_date: false,
    product_label: "",
    contract_label: "",
    style_label: "",
    style_display: "",
    period_label: "",
    ...overrides,
  };
}
